import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CompanyUserProvider } from '@lighty';
import { StorageService, TranslateService } from '@services';
import { CommonToastService } from '@common2/services/toast.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'msc-organize-user-modal-massive-invitation',
    templateUrl: './massive-invitation.component.html'
})
export class OrganizeUserModalMassiveInvitationComponent implements OnInit, OnDestroy {
    @Input() counts: any;
    @Output() onClose: EventEmitter<any> = new EventEmitter();

    public isActive: boolean = false;
    public isLoading: boolean = true;
    public isSending: boolean = false;
    public invitations: any[] = [];
    public selectedIds: Set<number> = new Set();
    public searchQuery: string = '';
    public currentPage: number = 1;
    public totalPages: number = 1;
    public totalCount: number = 0;

    private company: any;
    private searchSubject: Subject<string> = new Subject();
    private destroy$: Subject<void> = new Subject();

    constructor(
        private companyUserProvider: CompanyUserProvider,
        private storageService: StorageService,
        private translateService: TranslateService,
        private toastService: CommonToastService,
    ) { }

    ngOnInit(): void {
        this.isActive = true;
        this.company = this.storageService.get('company');

        this.searchSubject.pipe(
            debounceTime(300),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.loadInvitations(1);
        });

        this.loadInvitations();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadInvitations(page: number = 1): void {
        this.isLoading = true;
        const params: any = { page, per_page: 20 };
        if (this.searchQuery) {
            params.search = this.searchQuery;
        }

        this.companyUserProvider.getInvitationsList(this.company.id, params).subscribe({
            next: (data: any) => {
                const pagination = data.pagination || {};
                this.invitations = Object.keys(data)
                    .filter(k => !isNaN(Number(k)))
                    .map(k => data[k]);
                this.totalCount = pagination.total ?? 0;
                this.totalPages = pagination.lastPage ?? 1;
                this.currentPage = pagination.currentPage ?? page;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    onSearch(value: string): void {
        this.searchQuery = value;
        this.searchSubject.next(value);
    }

    toggleSelect(id: number): void {
        if (this.selectedIds.has(id)) {
            this.selectedIds.delete(id);
        } else {
            this.selectedIds.add(id);
        }
    }

    isSelected(id: number): boolean {
        return this.selectedIds.has(id);
    }

    get allCurrentPageSelected(): boolean {
        return this.invitations.length > 0 && this.invitations.every(u => this.selectedIds.has(u.id));
    }

    toggleSelectAll(): void {
        if (this.allCurrentPageSelected) {
            this.invitations.forEach(u => this.selectedIds.delete(u.id));
        } else {
            this.invitations.forEach(u => this.selectedIds.add(u.id));
        }
    }

    get selectedCount(): number {
        return this.selectedIds.size;
    }

    get pages(): number[] {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }

    resendSelected(): void {
        if (!this.selectedIds.size || this.isSending) { return; }
        this.isSending = true;
        const payload = { type: 'normal', account_ids: [...this.selectedIds] };
        this.companyUserProvider.sendInvitation(this.company.slug, payload).subscribe({
            next: () => {
                this.isSending = false;
                this.selectedIds.clear();
                this.toastService.onSuccess(this.translateService.instant('toast.invitations-sent'));
            },
            error: () => { this.isSending = false; }
        });
    }

    deleteSelected(): void {
        if (!this.selectedIds.size || this.isSending) { return; }
        this.isSending = true;
        this.companyUserProvider.deleteInvitations(this.company.id, { account_ids: [...this.selectedIds] }).subscribe({
            next: () => {
                this.isSending = false;
                this.selectedIds.clear();
                this.loadInvitations(this.currentPage);
                this.toastService.onSuccess(this.translateService.instant('toast.invitations-deleted'));
            },
            error: () => { this.isSending = false; }
        });
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages || page === this.currentPage) { return; }
        this.loadInvitations(page);
    }

    close(): void {
        this.onClose.emit();
    }
}
